import React, { useState } from "react";
import { formatPrice } from "../../../../utils/priceFormatter";
import {
  FaBolt,
  FaTruck,
  FaShieldAlt,
  FaChevronUp,
  FaRedoAlt,
  FaGift,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { Drawer, Divider, Tag } from "antd";
import { CloseOutlined } from "@ant-design/icons";

const ProductHeader = ({ productData, isDarkMode, onSale }) => {
  const navigate = useNavigate();
  const [drawerVisible, setDrawerVisible] = useState(false);

  // Card data defined in JSON
  const cardsData = [
    {
      id: 1,
      label: "Instant Payment",
      icon: FaBolt,
      lightMode: {
        bgColor: "bg-green-50",
        textColor: "text-green-700",
        iconColor: "text-green-600",
        borderColor: "border-green-200",
      },
      darkMode: {
        bgColor: "bg-green-900/20",
        textColor: "text-green-300",
        iconColor: "text-green-400",
        borderColor: "border-green-800",
      },
    },
    {
      id: 2,
      label: "Free Express Pickup",
      icon: FaTruck,
      lightMode: {
        bgColor: "bg-blue-50",
        textColor: "text-blue-700",
        iconColor: "text-blue-600",
        borderColor: "border-blue-200",
      },
      darkMode: {
        bgColor: "bg-blue-900/20",
        textColor: "text-blue-300",
        iconColor: "text-blue-400",
        borderColor: "border-blue-800",
      },
    },
    {
      id: 3,
      label: "100% Safe",
      icon: FaShieldAlt,
      lightMode: {
        bgColor: "bg-purple-50",
        textColor: "text-purple-700",
        iconColor: "text-purple-600",
        borderColor: "border-purple-200",
      },
      darkMode: {
        bgColor: "bg-purple-900/20",
        textColor: "text-purple-300",
        iconColor: "text-purple-400",
        borderColor: "border-purple-800",
      },
    },
  ];

  const handleRecalculate = () => {
    navigate(-1);
  };

  const handleSellNow = () => {
    if (onSale) {
      onSale();
    }
  };

  const openDrawer = () => {
    setDrawerVisible(true);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
  };

  // Get card styles based on dark mode
  const getCardStyles = (card) => {
    if (isDarkMode) {
      return {
        bgColor: card.darkMode.bgColor,
        textColor: card.darkMode.textColor,
        iconColor: card.darkMode.iconColor,
        borderColor: card.darkMode.borderColor,
      };
    }
    return {
      bgColor: card.lightMode.bgColor,
      textColor: card.lightMode.textColor,
      iconColor: card.lightMode.iconColor,
      borderColor: card.lightMode.borderColor,
    };
  };

  // Get product image URL
  const getProductImage = () => {
    if (productData?.product?.image) {
      if (productData.product.image.startsWith('http')) {
        return productData.product.image;
      }
      return `https://app.tap2cash.in/uploads/${productData.product.image}`;
    }
    return "https://app.tap2cash.in/uploads/products/20-08-24-03-18-39-apple-iphone-15-plus.PNG";
  };

  // Get product name
  const getProductName = () => {
    return productData?.variation?.title || 
           productData?.variationName || 
           productData?.product?.title || 
           "Product";
  };

  // Get final amount
  const getFinalAmount = () => {
    return productData?.evaluated_amount || 
           productData?.finalAmount || 
           0;
  };

  // Get coupon bonus
  const getCouponBonus = () => {
    return productData?.coupon_bonus_amount || 0;
  };

  // Check if coupon is applied
  const hasCoupon = () => {
    return productData?.has_coupon_applied || false;
  };

  // Get coupon code
  const getCouponCode = () => {
    return productData?.coupon_code || null;
  };

  const productImage = getProductImage();
  const productName = getProductName();
  const finalAmount = getFinalAmount();
  const couponBonus = getCouponBonus();
  const isCouponApplied = hasCoupon();
  const couponCode = getCouponCode();

  // Calculate total with coupon
  const totalWithCoupon = finalAmount + couponBonus;

  return (
    <>
      <div className={`${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}>
        <div className="p-3 md:p-8">
          {productData && (
            <>
              {/* Mobile Layout */}
              <div className="block md:hidden">
                {/* Product Header with Image and Title */}
                <div className="flex gap-3">
                  <div className="w-1/3 overflow-hidden shadow-md rounded-xl">
                    <img
                      src={productImage}
                      alt={productName}
                      className="object-cover w-full h-auto"
                      onError={(e) => {
                        e.target.src = "https://demo.tap2cash.in/uploads/products/20-08-24-03-18-39-apple-iphone-15-plus.PNG";
                      }}
                    />
                  </div>
                  <div className="w-2/3">
                    <h1
                      className={`text-sm font-bold mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {productName}
                    </h1>
                    <div className="flex flex-col mt-1">
                      <p
                        className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                      >
                        Selling Price
                      </p>
                      <div className="flex items-center justify-between mt-1">
                        <p
                          className={`text-2xl font-bold ${isDarkMode ? "text-green-500" : "text-green-600"}`}
                        >
                          {formatPrice(finalAmount)}
                        </p>
                        <button
                          onClick={handleRecalculate}
                          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 ${
                            isDarkMode
                              ? "bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700"
                              : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 shadow-sm"
                          }`}
                        >
                          <FaRedoAlt className="text-xs" />
                          <span>Recalculate</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Cards Grid - 3 in a row */}
                <div className="grid grid-cols-3 gap-2 my-6">
                  {cardsData.map((card) => {
                    const Icon = card.icon;
                    const styles = getCardStyles(card);
                    return (
                      <div
                        key={card.id}
                        className={`flex flex-col items-center justify-center gap-1.5 p-2 rounded-lg border transition-all duration-200 ${styles.bgColor} ${styles.borderColor}`}
                      >
                        <Icon className={`text-lg ${styles.iconColor}`} />
                        <span
                          className={`text-[10px] font-medium text-center ${styles.textColor}`}
                        >
                          {card.label}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Coupon Bonus Badge - Mobile */}
                {isCouponApplied && couponBonus > 0 && (
                  <div className={`flex items-center gap-2 px-3 py-2 mb-4 rounded-lg border ${isDarkMode ? 'bg-green-900/20 border-green-800' : 'bg-green-50 border-green-200'}`}>
                    <FaGift className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                      Coupon {couponCode && `(${couponCode})`} bonus: +{formatPrice(couponBonus)}
                    </span>
                  </div>
                )}

                <button
                  onClick={() => {
                    closeDrawer();
                    handleSellNow();
                  }}
                  className="group relative w-full flex items-center justify-center gap-3 py-3.5 font-semibold text-white transition-all duration-300 bg-gradient-to-r from-green-600 to-emerald-500 rounded-xl hover:from-green-700 hover:to-emerald-600 hover:shadow-lg hover:shadow-green-500/25 transform hover:-translate-y-0.5 active:translate-y-0 overflow-hidden"
                >
                  <span>Sell Now</span>
                  <div className="absolute inset-0 w-0 transition-all duration-300 bg-white/20 group-hover:w-full"></div>
                </button>

                {/* Fixed Bottom Bar for Mobile */}
                <div className="fixed bottom-0 left-0 right-0 z-50">
                  <div
                    className={`shadow-xl border-t transition-all duration-200 ${
                      isDarkMode
                        ? "bg-gray-900 border-gray-800"
                        : "bg-white border-gray-200"
                    }`}
                  >
                    {/* Price Summary Row */}
                    <div
                      className={`px-4 py-3 flex justify-between items-center border-b ${
                        isDarkMode ? "border-gray-800" : "border-gray-100"
                      }`}
                    >
                      <div>
                        <p
                          className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          Final Price
                        </p>
                        <div className="flex items-center gap-2">
                          <p
                            className={`text-xl font-bold ${isDarkMode ? "text-green-500" : "text-green-600"}`}
                          >
                            {formatPrice(totalWithCoupon)}
                          </p>
                          {isCouponApplied && couponBonus > 0 && (
                            <Tag color="success" className="text-xs bg-green-500 border-none">
                              +{formatPrice(couponBonus)}
                            </Tag>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p
                          className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          Express Pickup
                        </p>
                        <Tag
                          color="success"
                          className="mt-1 bg-green-500 border-none"
                        >
                          Free
                        </Tag>
                      </div>
                    </div>

                    {/* Action Buttons Row */}
                    <div className="flex">
                      <button
                        onClick={openDrawer}
                        className={`flex-1 flex items-center justify-center gap-2 py-4 text-sm font-semibold transition-all duration-200 ${
                          isDarkMode
                            ? "bg-gray-800 text-gray-300 hover:bg-gray-750"
                            : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        <span>Show Breakdown</span>
                        <FaChevronUp className="text-xs" />
                      </button>
                      <button
                        onClick={handleSellNow}
                        className="flex items-center justify-center flex-1 gap-2 py-4 text-base font-semibold text-white transition-all duration-200 shadow-lg bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600"
                      >
                        <span>Sell Now</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop Layout */}
              <div className="hidden gap-6 md:grid md:grid-cols-3">
                {/* Left Section - Product Details (spans 2 columns) */}
                <div className="md:col-span-2">
                  <div
                    className={`grid grid-cols-2 gap-5 p-5 rounded-xl ${
                      isDarkMode ? "bg-gray-800" : "bg-white"
                    } shadow-sm border ${
                      isDarkMode ? "border-gray-700" : "border-gray-100"
                    }`}
                  >
                    {/* Product Image */}
                    <div className={`overflow-hidden rounded-lg`}>
                      <img
                        src={productImage}
                        alt={productName}
                        className="object-contain w-full h-[250px]"
                        onError={(e) => {
                          e.target.src = "https://demo.tap2cash.in/uploads/products/20-08-24-03-18-39-apple-iphone-15-plus.PNG";
                        }}
                      />
                    </div>

                    {/* Product Info */}
                    <div>
                      <h2
                        className={`text-2xl font-semibold tracking-wide mb-3 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {productName}
                      </h2>

                      <div className="flex flex-col">
                        <div className="flex items-center justify-between">
                          <div>
                            <p
                              className={`text-xs uppercase tracking-wider ${
                                isDarkMode ? "text-gray-500" : "text-gray-400"
                              }`}
                            >
                              Selling Price
                            </p>
                            <p
                              className={`text-3xl font-semibold ${
                                isDarkMode ? "text-green-400" : "text-green-600"
                              }`}
                            >
                              {formatPrice(finalAmount)}
                            </p>
                          </div>
                          <button
                            onClick={handleRecalculate}
                            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg ${
                              isDarkMode
                                ? "bg-gray-700 text-gray-300 border border-gray-600"
                                : "bg-white text-gray-700 border border-gray-200"
                            }`}
                          >
                            <FaRedoAlt className="text-sm" />
                            <span>Recalculate</span>
                          </button>
                        </div>
                      </div>

                      {/* Feature Cards */}
                      <div className="grid grid-cols-3 gap-3 mt-6">
                        {cardsData.map((card) => {
                          const Icon = card.icon;
                          const styles = getCardStyles(card);
                          return (
                            <div
                              key={card.id}
                              className={`flex flex-col items-center justify-center gap-2 px-3 py-4 rounded-lg ${styles.bgColor} ${styles.borderColor}`}
                            >
                              <Icon className={`text-xl ${styles.iconColor}`} />
                              <span
                                className={`text-xs font-medium text-center tracking-wide ${styles.textColor}`}
                              >
                                {card.label}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Section - Price Card (spans 1 column) */}
                <div className="md:col-span-1">
                  <div
                    className={`p-6 rounded-xl ${
                      isDarkMode ? "bg-gray-800" : "bg-white"
                    } shadow-sm border ${
                      isDarkMode ? "border-gray-700" : "border-gray-100"
                    }`}
                  >
                    {/* Price Summary Heading */}
                    <h3
                      className={`text-lg font-semibold tracking-wide mb-5 ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Price Summary
                    </h3>

                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span
                          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          Selling Price
                        </span>
                        <span
                          className={`text-xl font-medium ${isDarkMode ? "text-green-400" : "text-green-600"}`}
                        >
                          {formatPrice(finalAmount)}
                        </span>
                      </div>

                      {/* Coupon Bonus - Desktop */}
                      {isCouponApplied && couponBonus > 0 && (
                        <div className="flex items-center justify-between">
                          <span
                            className={`text-sm flex items-center gap-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            <FaGift className="text-green-500" />
                            Coupon Bonus {couponCode && `(${couponCode})`}
                          </span>
                          <span
                            className={`text-sm font-medium ${isDarkMode ? "text-green-400" : "text-green-600"}`}
                          >
                            +{formatPrice(couponBonus)}
                          </span>
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <span
                          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          Express Pickup
                        </span>
                        <Tag
                          color="success"
                          className="px-3 py-0.5 text-xs font-medium bg-green-500 border-none"
                        >
                          Free
                        </Tag>
                      </div>

                      <Divider
                        className={`my-3 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                      />

                      <div className="flex items-center justify-between pt-2">
                        <span
                          className={`text-base font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          Total Price
                        </span>
                        <span
                          className={`text-2xl font-semibold ${isDarkMode ? "text-green-400" : "text-green-600"}`}
                        >
                          {formatPrice(totalWithCoupon)}
                        </span>
                      </div>

                      {/* Sell Now Button */}
                      <button
                        onClick={() => {
                          closeDrawer();
                          handleSellNow();
                        }}
                        className={`flex items-center justify-center w-full gap-3 py-3.5 mt-4 font-medium text-white rounded-lg bg-green-600 hover:bg-green-700 transition-colors`}
                      >
                        <span>Sell Now</span>
                      </button>

                      {/* Premium subtle badge */}
                      <p
                        className={`text-center text-xs ${isDarkMode ? "text-gray-600" : "text-gray-400"} tracking-wide`}
                      >
                        Instant payment • 100% secure
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Mobile Bottom Drawer for Price Breakdown */}
      <Drawer
        title="Price Breakdown"
        placement="bottom"
        onClose={closeDrawer}
        open={drawerVisible}
        height="auto"
        className={isDarkMode ? "dark-drawer" : ""}
        closeIcon={<CloseOutlined className={isDarkMode ? "text-white" : ""} />}
        headerStyle={{
          borderBottom: `1px solid ${isDarkMode ? "#374151" : "#f0f0f0"}`,
          background: isDarkMode ? "#1f2937" : "#fff",
          color: isDarkMode ? "#fff" : "#000",
        }}
        bodyStyle={{
          background: isDarkMode ? "#1f2937" : "#fff",
          padding: "20px",
        }}
      >
        <div className="space-y-4">
          <div
            className={`p-4 rounded-xl ${isDarkMode ? "bg-gray-800" : "bg-gray-50"}`}
          >
            <div className="flex items-center justify-between mb-3">
              <span className={isDarkMode ? "text-gray-300" : "text-gray-700"}>
                Selling Price
              </span>
              <span
                className={`text-xl font-bold ${isDarkMode ? "text-green-500" : "text-green-600"}`}
              >
                {formatPrice(finalAmount)}
              </span>
            </div>

            {/* Coupon Bonus in Drawer */}
            {isCouponApplied && couponBonus > 0 && (
              <div className="flex items-center justify-between mb-3">
                <span className={`flex items-center gap-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  <FaGift className="text-green-500" />
                  Coupon Bonus {couponCode && `(${couponCode})`}
                </span>
                <span
                  className={`text-lg font-medium ${isDarkMode ? "text-green-400" : "text-green-600"}`}
                >
                  +{formatPrice(couponBonus)}
                </span>
              </div>
            )}

            <div className="flex items-center justify-between">
              <span className={isDarkMode ? "text-gray-300" : "text-gray-700"}>
                Express Pickup
              </span>
              <Tag color="success" className="bg-green-500 border-none">
                Free
              </Tag>
            </div>

            <Divider
              className={`my-3 ${isDarkMode ? "border-gray-700" : ""}`}
            />

            <div className="flex items-center justify-between">
              <span
                className={`text-base font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Total Price
              </span>
              <span
                className={`text-2xl font-bold ${isDarkMode ? "text-green-500" : "text-green-600"}`}
              >
                {formatPrice(totalWithCoupon)}
              </span>
            </div>
          </div>
        </div>
      </Drawer>

      <style jsx>{`
        .dark-drawer .ant-drawer-header {
          background: #1f2937;
          border-bottom-color: #374151;
        }

        .dark-drawer .ant-drawer-title {
          color: white;
        }

        .dark-drawer .ant-drawer-body {
          background: #1f2937;
        }

        .dark-drawer .ant-divider {
          border-color: #374151;
        }

        * {
          transition-property:
            background-color, border-color, color, fill, stroke, opacity,
            box-shadow, transform;
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
          transition-duration: 150ms;
        }
      `}</style>
    </>
  );
};

export default ProductHeader;